import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useTheme } from "../../contexts/ThemeContext";
import { CLASS_COLORS, type DwellTrendPoint } from "../../services/peopleDistributionTypes";
import { getLocalDateString } from "../../services/peopleDistributionTypes";
import {
  buildTimeAxisConfig,
  buildTrendChartDataZoom,
  getRecentWindowDataZoomRange,
  toTimeSeriesData,
} from "./chartTimeAxis";

type DwellTrendChartProps = {
  data: DwellTrendPoint[];
  selectedDate?: string;
};

export const DwellTrendChart: React.FC<DwellTrendChartProps> = ({
  data,
  selectedDate = getLocalDateString(),
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const axisColor = isDark ? "#94A3B8" : "#64748B";
  const gridColor = isDark ? "#334155" : "#E2E8F0";
  const tooltipBg = isDark ? "#1e293b" : "#fff";
  const tooltipText = isDark ? "#e2e8f0" : "#0f172a";

  const option = useMemo(() => {
    const bucketTimes = data.map((point) => point.bucketTime);
    const values = data.map((point) => point.weightedAvgDwell);
    const seriesData = toTimeSeriesData(bucketTimes, values);

    let peakIndex = -1;
    let peakValue = 0;
    values.forEach((value, index) => {
      if (value >= peakValue) {
        peakValue = value;
        peakIndex = index;
      }
    });

    const peakTimestamp =
      peakIndex >= 0 ? new Date(data[peakIndex].bucketTime).getTime() : null;

    const { start: zoomStart, end: zoomEnd } = getRecentWindowDataZoomRange(
      bucketTimes,
      selectedDate,
      timezone
    );

    return {
      tooltip: {
        trigger: "axis",
        backgroundColor: tooltipBg,
        borderColor: gridColor,
        textStyle: { color: tooltipText, fontSize: 12 },
        axisPointer: { type: "line" },
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          const first = items[0] as { value?: [number, number] };
          const value = first?.value?.[1] ?? 0;
          const time = first?.value?.[0]
            ? new Intl.DateTimeFormat("en-US", {
                timeZone: timezone,
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              }).format(new Date(first.value[0]))
            : "";
          return `${time}<br/>Avg dwell: <strong>${value}s</strong>`;
        },
      },
      grid: { left: 48, right: 16, top: 32, bottom: 56 },
      dataZoom: buildTrendChartDataZoom(isDark, axisColor, "#7C3AED", zoomStart, zoomEnd),
      xAxis: buildTimeAxisConfig(selectedDate, timezone, axisColor, gridColor),
      yAxis: {
        type: "value",
        name: "seconds",
        axisLine: { show: false },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
        axisLabel: {
          color: axisColor,
          fontSize: 11,
          formatter: (value: number) => `${value}s`,
        },
      },
      series: [
        {
          name: "Avg dwell",
          type: "line",
          smooth: true,
          symbol: "none",
          lineStyle: { width: 2.5, color: "#7C3AED" },
          itemStyle: { color: "#7C3AED" },
          areaStyle: { color: "#7C3AED", opacity: 0.1 },
          data: seriesData,
          markLine:
            peakTimestamp != null
              ? {
                  silent: true,
                  symbol: "none",
                  lineStyle: { color: CLASS_COLORS.peak, type: "dashed", width: 1.5 },
                  label: {
                    formatter: "Peak",
                    color: CLASS_COLORS.peak,
                    fontSize: 11,
                    position: "end",
                  },
                  data: [{ xAxis: peakTimestamp }],
                }
              : undefined,
        },
      ],
    };
  }, [data, selectedDate, timezone, isDark, axisColor, gridColor, tooltipBg, tooltipText]);

  if (!data.length) {
    return (
      <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        No dwell trend data available.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Dwell trend</h3>
        <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:border-slate-600 dark:text-slate-400">
          weighted avg
        </span>
      </div>
      <ReactECharts option={option} style={{ height: 280, width: "100%" }} notMerge lazyUpdate />
    </div>
  );
};

export default DwellTrendChart;
